import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { logHistory } from './historyLog';
import { scrubForFirestoreValue } from './scrubFirestore';

interface Options {
  /** Debounce delay in ms before flushing the queued field updates. */
  debounceMs?: number;
}

interface UseLeadDraftReturn {
  /** Queue a single field update (dotted path supported). Triggers debounced flush + history log. */
  queueField: (field: string, value: unknown) => void;
  /** Force flush of any queued updates. Throws if the write fails. */
  flush: () => Promise<void>;
  /** True when the debounced queue has unsaved field updates. */
  hasPending: () => boolean;
  /** True while a flush is in flight. */
  saving: boolean;
  /** ISO timestamp of the last successful flush, if any. */
  lastSavedAt: string | null;
  /** Last save error, cleared on next successful save. */
  error: Error | null;
}

/**
 * Wizard-friendly debounced lead patcher. Every queued field is logged in the
 * lead's history sub-collection on flush, satisfying the v1.9 "Zero-Leak"
 * requirement (every typed character batch is archived).
 *
 * Dotted field paths are supported via Firestore's native nested update syntax
 * (e.g. queueField('engagement.urgency.tier', 'Rush')).
 */
export function useLeadDraft(leadId: string, options: Options = {}): UseLeadDraftReturn {
  const { user } = useAuth();
  const debounceMs = options.debounceMs ?? 900;

  const queueRef = useRef<Map<string, unknown>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const queue = queueRef.current;
    if (queue.size === 0) return;

    const entries = Array.from(queue.entries());
    queueRef.current = new Map();

    setSaving(true);
    setError(null);
    try {
      const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
      for (const [field, value] of entries) {
        const cleaned = scrubForFirestoreValue(value);
        if (cleaned !== undefined) {
          patch[field] = cleaned;
        }
      }
      await updateDoc(doc(db, 'leads', leadId), patch);

      logHistory({
        leadId,
        user,
        kind: 'wizard_field',
        payload: {
          fields: entries.map(([field, value]) => ({
            field,
            value: scrubForFirestoreValue(value),
          })),
        },
      });

      setLastSavedAt(new Date().toISOString());
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      // eslint-disable-next-line no-console
      console.error('[flashpoint] lead draft save failed', e);
      // re-queue so a later flush can retry
      for (const [field, value] of entries) {
        if (!queueRef.current.has(field)) queueRef.current.set(field, value);
      }
      throw e;
    } finally {
      setSaving(false);
    }
  }, [leadId, user]);

  const hasPending = useCallback(() => queueRef.current.size > 0, []);

  const queueField = useCallback(
    (field: string, value: unknown) => {
      queueRef.current.set(field, value);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, debounceMs);
    },
    [flush, debounceMs],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { queueField, flush, hasPending, saving, lastSavedAt, error };
}
